import React, { useState, useEffect } from 'react';
import { Box, Alert, Container, CircularProgress } from '@mui/material';
import { format, addDays } from 'date-fns';
import { useQuery } from '@tanstack/react-query';

import {
  getMeterSummaries,
  MeterSummaryResult,
  MeterEventSummary,
} from '../../api/hooks/meters/fetchMeterSummaries';
import { SectionPaper } from './SectionPaper';
import { MetricCard } from './MetricCard';
import { DateRangeFilter } from './DateRangeFilter';
import { AnalyticsSection } from './AnalyticsSection';
import { formatMeterDuration } from '../../common/commonUtils';
import { getErrorMessage } from '../../common/errorUtils';
import { useIsMobile } from '../../api/hooks/isMobile/isMobile';

const VOICEMAIL_HOURLY_RATE = 3.3;
const RPC_HOURLY_RATE = 5.5;
const RVM_RATE = 0.14;
const RVM_BATCH_SIZE = 10;
const SECONDS_PER_HOUR = 3600;
const TIMEZONE_OFFSET = -4; // EST offset from UTC
const MIN_ALLOWED_DATE = new Date('2025-03-24T04:00:00.000Z'); // 00:00 EST = 04:00 UTC
const INBOUND_CALL_HOURLY_RATE = 6.0;
const METER_ID_CONSUEGRA_INBOUND_CALL = 'mtr_61SUpRTt4YugSjH2b41Kx3vje6v88WQa';
const METER_ID_VOICEMAIL_DURATION = 'mtr_61S8Zs6LJI6hSwiyq41Kx3vje6v88UtU';
const METER_ID_DROPPED_VOICEMAILS = 'mtr_61S8ZsA6iYsRBDR1W41Kx3vje6v88Ecq';
const METER_ID_RPC_CALL_DURATION = 'mtr_61S8Zs27i7dMP4NUj41Kx3vje6v88BKK';
const METER_ID_RPC_CALL_COUNT = 'mtr_61S8ZsE0afbj2JOPt41Kx3vje6v88Wim';

interface GroupedDataItem {
  start_time: number;
  aggregated_value: number;
  displayDate: string;
}

interface ChartDataItem {
  time: string;
  value: number;
  startTime: number;
}

interface RvmCostDataItem extends GroupedDataItem {
  rvm_cost: number;
}

interface Summary {
  totalSeconds: number;
  totalVoicemails: number;
  voicemailHours: number;
  rpcHours: number;
  voicemailCost: number;
  rpcCost: number;
  avgTimePerVoicemail: number;
  avgCostPerVoicemail: number;
  totalRpcSeconds: number;
  totalRpcCalls: number;
  avgRpcDuration: number;
  totalRvmCost: number;
  rvmCostData: RvmCostDataItem[];
  inboundHours: number;
  inboundCost: number;
  inboundMinutes: number;
}

const Usage: React.FC = () => {
  const [usageData, setUsageData] = useState<MeterSummaryResult[] | null>(null);
  const [filteredData, setFilteredData] = useState<MeterSummaryResult[] | null>(
    null
  );
  const isMobile = useIsMobile();
  const [summary, setSummary] = useState<Summary | null>(null);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  const toEST = (date: Date | null): Date => {
    if (!date) return new Date();
    return new Date(date.getTime() + TIMEZONE_OFFSET * SECONDS_PER_HOUR * 1000);
  };

  const fromEST = (date: Date | null): Date => {
    if (!date) return new Date();
    return new Date(date.getTime() - TIMEZONE_OFFSET * SECONDS_PER_HOUR * 1000);
  };

  const startOfDayEST = (date: Date): Date => {
    const estDate = toEST(date);
    return fromEST(
      new Date(estDate.getFullYear(), estDate.getMonth(), estDate.getDate())
    );
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const groupDataByTime = (data: MeterEventSummary[]): GroupedDataItem[] => {
    const groupedData = new Map<string, GroupedDataItem>();

    data.forEach((item) => {
      const itemDate = toEST(new Date(item.start_time * 1000));
      if (!itemDate) return;

      const dayKey = format(itemDate, 'yyyy-MM-dd');

      const existing = groupedData.get(dayKey) || {
        start_time: item.start_time,
        aggregated_value: 0,
        displayDate: format(itemDate, 'MMM d'),
      };

      existing.aggregated_value += item.aggregated_value;
      groupedData.set(dayKey, existing);
    });

    return Array.from(groupedData.values()).sort(
      (a, b) => a.start_time - b.start_time
    );
  };

  const getMeterTitle = (meterId: string): string => {
    switch (meterId) {
      case METER_ID_VOICEMAIL_DURATION:
        return 'Voicemail Duration';
      case METER_ID_DROPPED_VOICEMAILS:
        return 'Dropped Voicemails';
      case METER_ID_RPC_CALL_DURATION:
        return 'RPC Call Duration';
      case METER_ID_RPC_CALL_COUNT:
        return 'RPC Call Count';
      default:
        return meterId;
    }
  };

  const isDurationMeter = (meterId: string): boolean => {
    return (
      meterId === METER_ID_VOICEMAIL_DURATION ||
      meterId === METER_ID_RPC_CALL_DURATION
    );
  };

  const {
    data: meterSummaries = [],
    isFetching: meterSummariesLoading,
    error: meterSummariesError,
  } = useQuery({
    queryKey: ['meterSummaries', startDate, endDate],
    retry: false,
    refetchOnWindowFocus: false,
    queryFn: () => {
      const startTimestamp = startDate
        ? Math.floor(startDate.getTime() / 1000)
        : undefined;
      const endTimestamp = endDate
        ? Math.floor(endDate.getTime() / 1000)
        : undefined;
      const startISO = startDate?.toISOString();
      const endISO = endDate?.toISOString();

      console.log('Selected dates:', {
        start: startDate ? format(startDate, 'yyyy-MM-dd') : 'undefined',
        end: endDate ? format(endDate, 'yyyy-MM-dd') : 'undefined',
      });
      console.log('Sending timestamps:', {
        startTimestamp,
        endTimestamp,
        startISO,
        endISO,
      });

      return getMeterSummaries({
        startTime: startTimestamp,
        endTime: endTimestamp,
        startISO: startISO,
        endISO: endISO,
      });
    },
    enabled: !!startDate && !!endDate,
  });

  useEffect(() => {
    const now = new Date();
    const estNow = toEST(now);
    if (!estNow) return;

    const mostRecentSunday = startOfDayEST(addDays(estNow, -estNow.getDay()));
    const previousMonday = startOfDayEST(addDays(mostRecentSunday, -6));

    setStartDate(previousMonday);
    setEndDate(mostRecentSunday);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleStartDateChange = (newValue: Date | null) => {
    if (!newValue) return;
    setStartDate(newValue);
  };

  const handleEndDateChange = (newValue: Date | null) => {
    if (!newValue) return;
    console.log('Setting end date', newValue);
    setEndDate(newValue);
  };

  const fillTimeGaps = (
    data: GroupedDataItem[],
    start: Date,
    end: Date
  ): GroupedDataItem[] => {
    const result = new Map<string, GroupedDataItem>();

    // Create a map of existing data points
    data.forEach((item) => {
      const date = toEST(new Date(item.start_time * 1000));
      if (!date) return;
      const dayKey = format(date, 'yyyy-MM-dd');
      result.set(dayKey, item);
    });

    const endDate = format(toEST(end), 'yyyy-MM-dd');
    let currentDate = startOfDayEST(start);

    while (format(toEST(currentDate), 'yyyy-MM-dd') <= endDate) {
      const dayKey = format(toEST(currentDate), 'yyyy-MM-dd');
      if (!result.has(dayKey)) {
        result.set(dayKey, {
          start_time: Math.floor(currentDate.getTime() / 1000),
          aggregated_value: 0,
          displayDate: format(toEST(currentDate), 'MMM d'),
        });
      }
      currentDate = addDays(currentDate, 1);
    }

    return Array.from(result.values())
      .sort((a, b) => a.start_time - b.start_time)
      .filter((item) => {
        const itemDate = format(
          toEST(new Date(item.start_time * 1000)),
          'yyyy-MM-dd'
        );
        return itemDate <= endDate;
      });
  };

  const processChartData = (meterData: MeterSummaryResult): ChartDataItem[] => {
    if (!meterData?.data?.data || !startDate || !endDate) return [];

    const groupedData = groupDataByTime(meterData.data.data);
    const filledData = fillTimeGaps(groupedData, startDate, endDate);

    // If this is the voicemail duration meter, we need to check the dropped voicemail count
    if (meterData.meterId === METER_ID_VOICEMAIL_DURATION) {
      // Find the dropped voicemail meter data
      const droppedVoicemailMeter = usageData?.find(
        (m) => m.meterId === METER_ID_DROPPED_VOICEMAILS
      );
      if (droppedVoicemailMeter) {
        const droppedVoicemailData = groupDataByTime(
          droppedVoicemailMeter.data.data
        );
        // Create a map of dates to voicemail counts
        const voicemailCountMap = new Map<string, number>(
          droppedVoicemailData.map((item) => [
            format(toEST(new Date(item.start_time * 1000)), 'yyyy-MM-dd'),
            item.aggregated_value,
          ])
        );

        // Zero out duration for days with less than 100 voicemails
        return filledData.map((item) => {
          const dayKey = format(
            toEST(new Date(item.start_time * 1000)),
            'yyyy-MM-dd'
          );
          const voicemailCount = voicemailCountMap.get(dayKey) || 0;
          return {
            time: item.displayDate,
            value: voicemailCount < 100 ? 0 : item.aggregated_value,
            startTime: item.start_time,
          };
        });
      }
    }

    // If this is the RPC duration meter, we need to check the RPC call count
    if (meterData.meterId === METER_ID_RPC_CALL_DURATION) {
      // Find the RPC call count meter data
      const rpcCountMeter = usageData?.find(
        (m) => m.meterId === METER_ID_RPC_CALL_COUNT
      );
      if (rpcCountMeter) {
        const rpcCountData = groupDataByTime(rpcCountMeter.data.data);
        // Create a map of dates to RPC counts
        const rpcCountMap = new Map<string, number>(
          rpcCountData.map((item) => [
            format(toEST(new Date(item.start_time * 1000)), 'yyyy-MM-dd'),
            item.aggregated_value,
          ])
        );

        // Zero out duration for days with less than 20 RPC calls
        return filledData.map((item) => {
          const dayKey = format(
            toEST(new Date(item.start_time * 1000)),
            'yyyy-MM-dd'
          );
          const rpcCount = rpcCountMap.get(dayKey) || 0;
          return {
            time: item.displayDate,
            value: rpcCount < 20 ? 0 : item.aggregated_value,
            startTime: item.start_time,
          };
        });
      }
    }

    // For dropped voicemail count, RPC call count, or other meters
    return filledData.map((item) => ({
      time: item.displayDate,
      value:
        (meterData.meterId === METER_ID_DROPPED_VOICEMAILS &&
          item.aggregated_value < 100) ||
        (meterData.meterId === METER_ID_RPC_CALL_COUNT &&
          item.aggregated_value < 20)
          ? 0
          : item.aggregated_value,
      startTime: item.start_time,
    }));
  };

  const filterAndUpdateData = (start: Date, end: Date) => {
    if (!usageData || !start || !end) return;

    const filteredMeters = usageData.map((meter) => ({
      ...meter,
      data: {
        ...meter.data,
        data:
          meter.data.data?.filter((item) => {
            const itemDate = format(
              toEST(new Date(item.start_time * 1000)) ?? new Date(),
              'yyyy-MM-dd'
            );
            const startDate = format(toEST(start) ?? new Date(), 'yyyy-MM-dd');
            const endDate = format(toEST(end) ?? new Date(), 'yyyy-MM-dd');
            return itemDate >= startDate && itemDate <= endDate;
          }) || [],
      },
    }));

    setFilteredData(filteredMeters);
    calculateSummary(filteredMeters);
  };

  const calculateSummary = (data: MeterSummaryResult[]) => {
    const durationMeter = data.find(
      (m) => m.meterId === METER_ID_VOICEMAIL_DURATION
    );
    const countMeter = data.find(
      (m) => m.meterId === METER_ID_DROPPED_VOICEMAILS
    );
    const rpcDurationMeter = data.find(
      (m) => m.meterId === METER_ID_RPC_CALL_DURATION
    );
    const rpcCountMeter = data.find(
      (m) => m.meterId === METER_ID_RPC_CALL_COUNT
    );
    const consuegraMeter = data.find(
      (m) => m.meterId === METER_ID_CONSUEGRA_INBOUND_CALL
    );

    const inboundCallData = consuegraMeter
      ? groupDataByTime(consuegraMeter.data.data || [])
      : [];
    const totalInboundSeconds = inboundCallData.reduce((sum, item) => {
      return sum + item.aggregated_value;
    }, 0);
    const inboundHours = Math.ceil(totalInboundSeconds / 3600);
    const inboundMinutes = Math.round(totalInboundSeconds / 60);
    const inboundCost = inboundHours * INBOUND_CALL_HOURLY_RATE;

    // Group data by day first
    const voicemailDurationData = durationMeter
      ? groupDataByTime(durationMeter.data.data)
      : [];
    const voicemailCountData = countMeter
      ? groupDataByTime(countMeter.data.data)
      : [];
    const rpcDurationData = rpcDurationMeter
      ? groupDataByTime(rpcDurationMeter.data.data)
      : [];
    const rpcCountData = rpcCountMeter
      ? groupDataByTime(rpcCountMeter.data.data)
      : [];

    // Create maps for filtering
    const voicemailCountMap = new Map<string, number>(
      voicemailCountData.map((item) => [
        format(toEST(new Date(item.start_time * 1000)), 'yyyy-MM-dd'),
        item.aggregated_value,
      ])
    );

    const rpcCountMap = new Map<string, number>(
      rpcCountData.map((item) => [
        format(toEST(new Date(item.start_time * 1000)), 'yyyy-MM-dd'),
        item.aggregated_value,
      ])
    );

    const allDataPoints = [
      ...voicemailDurationData,
      ...voicemailCountData,
      ...rpcDurationData,
      ...rpcCountData,
    ].filter((item) => item && item.start_time);

    const lastDataPoint = allDataPoints.sort(
      (a, b) => b.start_time - a.start_time
    )[0];

    const filterEndDate = lastDataPoint
      ? format(toEST(new Date(lastDataPoint.start_time * 1000)), 'yyyy-MM-dd')
      : format(toEST(new Date()), 'yyyy-MM-dd');

    // Apply the same filters as the charts and exclude the last day
    const totalSeconds = voicemailDurationData.reduce((sum, item) => {
      const dayKey = format(
        toEST(new Date(item.start_time * 1000)),
        'yyyy-MM-dd'
      );
      if (dayKey >= filterEndDate) return sum;
      const voicemailCount = voicemailCountMap.get(dayKey) || 0;
      return sum + (voicemailCount < 100 ? 0 : item.aggregated_value);
    }, 0);

    const totalVoicemails = voicemailCountData.reduce((sum, item) => {
      const dayKey = format(
        toEST(new Date(item.start_time * 1000)),
        'yyyy-MM-dd'
      );
      if (dayKey >= filterEndDate) return sum;
      const count = item.aggregated_value;
      return sum + (count < 100 ? 0 : count);
    }, 0);

    const totalRpcSeconds = rpcDurationData.reduce((sum, item) => {
      const dayKey = format(
        toEST(new Date(item.start_time * 1000)),
        'yyyy-MM-dd'
      );
      if (dayKey >= filterEndDate) return sum;
      const rpcCount = rpcCountMap.get(dayKey) || 0;
      return sum + (rpcCount < 20 ? 0 : item.aggregated_value);
    }, 0);

    const totalRpcCalls = rpcCountData.reduce((sum, item) => {
      const dayKey = format(
        toEST(new Date(item.start_time * 1000)),
        'yyyy-MM-dd'
      );
      if (dayKey >= filterEndDate) return sum;
      const count = item.aggregated_value;
      return sum + (count < 20 ? 0 : count);
    }, 0);

    // Convert seconds to billable hours (rounded up to nearest hour)
    const voicemailHours = Math.ceil(totalSeconds / SECONDS_PER_HOUR);
    const rpcHours = Math.ceil(totalRpcSeconds / SECONDS_PER_HOUR);

    // Calculate costs based on hours
    const voicemailCost = voicemailHours * VOICEMAIL_HOURLY_RATE;
    const rpcCost = rpcHours * RPC_HOURLY_RATE;

    const avgTimePerVoicemail =
      totalVoicemails > 0 ? totalSeconds / totalVoicemails : 0;
    const avgCostPerVoicemail =
      totalVoicemails > 0 ? voicemailCost / totalVoicemails : 0;
    const avgRpcDuration =
      totalRpcCalls > 0 ? totalRpcSeconds / totalRpcCalls : 0;

    // Calculate RVM costs per time period, applying the same filter
    const rvmCostData: RvmCostDataItem[] = voicemailCountData
      .filter((item) => {
        const dayKey = format(
          toEST(new Date(item.start_time * 1000)),
          'yyyy-MM-dd'
        );
        return dayKey < filterEndDate;
      })
      .map((item) => ({
        ...item,
        rvm_cost:
          item.aggregated_value < 100
            ? 0
            : Math.ceil(item.aggregated_value / RVM_BATCH_SIZE) * RVM_RATE,
      }));
    const totalRvmCost = rvmCostData.reduce(
      (sum, item) => sum + item.rvm_cost,
      0
    );

    setSummary({
      totalSeconds,
      totalVoicemails,
      voicemailHours,
      rpcHours,
      voicemailCost,
      rpcCost,
      avgTimePerVoicemail,
      avgCostPerVoicemail,
      totalRpcSeconds,
      totalRpcCalls,
      avgRpcDuration,
      totalRvmCost,
      rvmCostData,
      inboundHours,
      inboundCost,
      inboundMinutes,
    });
  };

  useEffect(() => {
    if (meterSummaries && meterSummaries.length > 0) {
      const shiftedData = meterSummaries.map((meter) => ({
        ...meter,
        data: {
          ...meter.data,
          data: (meter.data?.data || []).map((item) => ({
            ...item,
            start_time: item.start_time + 24 * 60 * 60,
          })),
        },
      }));

      setUsageData(shiftedData);
    }
  }, [meterSummaries]);

  useEffect(() => {
    if (usageData && startDate && endDate) {
      filterAndUpdateData(startDate, endDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usageData, startDate, endDate]);

  // Prepare data for components
  const costOverviewMetrics = summary
    ? [
        {
          title: 'Voicemail Cost',
          value: formatCurrency(
            (summary.voicemailCost || 0) + (summary.totalRvmCost || 0)
          ),
          subtitle: `${summary.voicemailHours || 0} hours at $${VOICEMAIL_HOURLY_RATE}/hr + RVM`,
        },
        {
          title: 'RPC Cost',
          value: formatCurrency(summary.rpcCost || 0),
          subtitle: `${summary.rpcHours || 0} hours at $${RPC_HOURLY_RATE}/hr`,
        },
        {
          title: 'Total Cost',
          value: formatCurrency(
            (summary.voicemailCost || 0) +
              (summary.rpcCost || 0) +
              (summary.totalRvmCost || 0)
          ),
          subtitle: 'Combined Services',
        },
        {
          title: 'Inbound Call Cost',
          value: formatCurrency(summary.inboundCost || 0),
          subtitle: `${summary.inboundHours || 0} hours at $${INBOUND_CALL_HOURLY_RATE}/hr`,
        },
      ]
    : [];

  const voicemailMetrics = summary
    ? [
        {
          title: 'Total Voicemails',
          value: summary.totalVoicemails || 0,
          subtitle: 'messages',
        },
        {
          title: 'Average Duration',
          value: formatMeterDuration(summary.avgTimePerVoicemail || 0),
          subtitle: 'per voicemail',
        },
        {
          title: 'RVM Infra Cost',
          value: formatCurrency(summary.totalRvmCost || 0),
          subtitle: `$${RVM_RATE} per ${RVM_BATCH_SIZE} voicemails`,
        },
      ]
    : [];

  const rpcMetrics = summary
    ? [
        {
          title: 'Total RPC Calls',
          value: summary.totalRpcCalls || 0,
          subtitle: 'calls',
        },
        {
          title: 'Average Duration',
          value: formatMeterDuration(summary.avgRpcDuration || 0),
          subtitle: 'per call',
        },
      ]
    : [];

  const inboundMetrics = summary
    ? [
        {
          title: 'Total Inbound Minutes',
          value: summary.inboundMinutes || 0,
          subtitle: 'minutes',
        },
        {
          title: 'Inbound Call Cost',
          value: formatCurrency(summary.inboundCost || 0),
          subtitle: `at $${INBOUND_CALL_HOURLY_RATE}/hour`,
        },
      ]
    : [];

  const voicemailMeterData =
    filteredData?.filter(
      (meter) =>
        meter.meterId === METER_ID_VOICEMAIL_DURATION ||
        meter.meterId === METER_ID_DROPPED_VOICEMAILS
    ) || [];

  const rpcMeterData =
    filteredData?.filter(
      (meter) =>
        meter.meterId === METER_ID_RPC_CALL_DURATION ||
        meter.meterId === METER_ID_RPC_CALL_COUNT
    ) || [];

  return (
    <Box sx={{ mt: 4, mb: 4 }}>
      {/* Header with Logo */}
      <Box
        display="flex"
        alignItems="center"
        mb={6}
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          pb: 3,
        }}
      >
        <img
          src="/collectwise-blue-logo-large.png"
          alt="CollectWise Logo"
          style={{
            height: '40px',
            marginRight: '24px',
          }}
        />
      </Box>

      {/* Date Range Filter */}
      <DateRangeFilter
        startDate={startDate}
        endDate={endDate}
        onStartDateChange={handleStartDateChange}
        onEndDateChange={handleEndDateChange}
        minDate={MIN_ALLOWED_DATE}
      />

      {meterSummariesError && (
        <Container maxWidth="lg" sx={{ mt: 4 }}>
          <Alert severity="error">{getErrorMessage(meterSummariesError)}</Alert>
        </Container>
      )}

      {meterSummariesLoading && (
        <Box display={'flex'} justifyContent={'center'}>
          <CircularProgress sx={{ mt: 4 }} />
        </Box>
      )}

      {filteredData && !meterSummariesLoading && (
        <>
          {/* Cost Overview */}
          <SectionPaper title="Cost Overview">
            <Box
              gap={3}
              display={'flex'}
              flexDirection={isMobile ? 'column' : 'row'}
              flexWrap={'wrap'}
            >
              {costOverviewMetrics.map((metric, index) => (
                <MetricCard
                  key={index}
                  title={metric.title}
                  value={metric.value}
                  subtitle={metric.subtitle}
                  variant="large"
                />
              ))}
            </Box>
          </SectionPaper>

          {/* Voicemail Analytics */}
          <AnalyticsSection
            title="Voicemail Analytics"
            metrics={voicemailMetrics}
            meterData={voicemailMeterData}
            processChartData={processChartData}
            getMeterTitle={getMeterTitle}
            isDurationMeter={isDurationMeter}
          />

          {/* RPC Analytics */}
          <AnalyticsSection
            title="RPC Analytics"
            metrics={rpcMetrics}
            meterData={rpcMeterData}
            processChartData={processChartData}
            getMeterTitle={getMeterTitle}
            isDurationMeter={isDurationMeter}
          />

          {/* Inbound Call Analytics */}
          {summary && !meterSummariesLoading && (
            <SectionPaper title="Inbound Call Analytics">
              <Box
                display={'flex'}
                gap={3}
                flexDirection={isMobile ? 'column' : 'row'}
                flexWrap={'wrap'}
              >
                {inboundMetrics.map((metric, index) => (
                  <MetricCard
                    key={index}
                    title={metric.title}
                    value={metric.value}
                    subtitle={metric.subtitle}
                    variant="large"
                  />
                ))}
              </Box>
            </SectionPaper>
          )}
        </>
      )}
    </Box>
  );
};

export default Usage;
